import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { healthAPI, telemetryAPI } from "../services/api";
import { clearCache } from "../utils/cache";

/**
 * CONNECTION STATUS (versi platform, disederhanakan).
 * ----------------------------------------------------------------------------
 * Dulu logika ini terikat ke Odoo (session/worker/hr_token). Sekarang app
 * standalone: cukup ping /health backend platform untuk tahu status koneksi.
 * Saat kembali online / window difokuskan, kita broadcast "hris:app-refresh"
 * supaya halaman memuat ulang data (absensi, saldo, dll).
 */
const ConnectionContext = createContext(null);

const ONLINE_POLL_MS = 60000;
const OFFLINE_RETRY_MS = 10000;
// Kalau /health butuh lebih lama dari ini untuk merespons, kita anggap "lambat"
// walau tetap berhasil — supaya user yang merasa laging tahu itu bukan aplikasi
// yang error, tapi koneksi/server yang lelet.
const SLOW_RESPONSE_MS = 2500;
// Satu kegagalan health-check BELUM berarti server mati (sinyal seluler sering
// putus-nyambung sesaat). Banner "server belum merespons" baru muncul setelah
// sekian kegagalan BERTURUT-TURUT; sebelum itu dicek ulang lebih cepat.
const FAILURES_BEFORE_DOWN = 2;
const QUICK_RECHECK_MS = 4000;
const MAX_TELEMETRY_EVENTS = 20;

/** Ringkas penyebab kegagalan untuk laporan (tanpa data pribadi). */
function failureReason(err) {
  if (err?.code === "ECONNABORTED") return "timeout";
  if (err?.response) return "http_" + err.response.status;
  return "network";
}

const STATUS_COPY = {
  checking: {
    title: "Mengecek koneksi",
    message: "Sebentar ya, aplikasi sedang memastikan backend bisa dihubungi.",
  },
  reconnecting: {
    title: "Menyambungkan ulang",
    message: "Koneksi sempat putus. Aplikasi sedang mencoba terhubung lagi.",
  },
  slow: {
    title: "Koneksi lambat",
    message: "Internet atau server sedang lambat merespons. Coba periksa sinyal/WiFi kamu — proses seperti absen mungkin butuh waktu lebih lama.",
  },
  offline: {
    title: "Koneksi terputus",
    message: "Perangkat kamu tidak terhubung ke internet. Coba periksa WiFi/data seluler",
  },
  server_down: {
    title: "Server belum merespons",
    message: "Internet perangkat kamu aktif, tapi server HR belum bisa dijangkau. aplikasi akan mencoba lagi otomatis.",
  },
  online: {
    title: "Terhubung",
    message: "Koneksi ke server HR sudah normal.",
  },
};

export function ConnectionProvider({ children }) {
  const [status, setStatus] = useState(navigator.onLine ? "checking" : "offline");
  const [lastOnlineAt, setLastOnlineAt] = useState(null);
  const lastStatus = useRef(status);
  const checkInFlight = useRef(false);
  const failures = useRef(0);          // kegagalan health-check berturut-turut
  const telemetry = useRef([]);        // kejadian yang belum dilaporkan ke server
  const lastTelemetryAt = useRef(0);
  const timer = useRef(null);

  const broadcastRefresh = useCallback((reason) => {
    clearCache("attendance_today");
    clearCache("leave_balance");
    clearCache("dashboard_summary");
    window.dispatchEvent(new CustomEvent("hris:app-refresh", { detail: { reason } }));
  }, []);

  const recordEvent = useCallback((status, reason, ms) => {
    telemetry.current.push({
      status,
      reason,
      ms: Math.round(ms),
      online: navigator.onLine,
      net: navigator.connection?.effectiveType || "",
      at: new Date().toISOString(),
      page: window.location.pathname,
    });
    if (telemetry.current.length > MAX_TELEMETRY_EVENTS) telemetry.current.shift();
  }, []);

  const flushTelemetry = useCallback(() => {
    if (!telemetry.current.length) return;
    // Kegagalan ("fail") langsung dilaporkan begitu koneksi pulih; kejadian "slow"
    // saja dibatasi maks 1 laporan / 2 menit agar tak menambah beban saat server lambat.
    const hasFail = telemetry.current.some((e) => e.status === "fail");
    if (!hasFail && Date.now() - lastTelemetryAt.current < 2 * 60 * 1000) return;
    lastTelemetryAt.current = Date.now();
    telemetryAPI.connection(telemetry.current.splice(0));
  }, []);

  const checkConnection = useCallback(
    async (reason = "poll") => {
      if (checkInFlight.current) return;
      checkInFlight.current = true;
      const previousStatus = lastStatus.current;

      if (!navigator.onLine) {
        setStatus("offline");
        checkInFlight.current = false;
        return;
      }

      setStatus((current) =>
        current === "offline" || current === "server_down" ? "reconnecting" : current
      );

      const startedAt = performance.now();
      try {
        await healthAPI.check();
        const elapsedMs = performance.now() - startedAt;
        const nextStatus = elapsedMs > SLOW_RESPONSE_MS ? "slow" : "online";
        failures.current = 0;
        if (nextStatus === "slow") recordEvent("slow", "slow_response", elapsedMs);
        setStatus(nextStatus);
        flushTelemetry(); // koneksi berhasil → aman melaporkan kejadian sebelumnya
        if (previousStatus === "offline" || previousStatus === "server_down" || previousStatus === "reconnecting") {
          setLastOnlineAt(new Date());
        }
        // Muat ulang data saat kembali fokus/online atau baru pulih dari gangguan.
        // Jangan broadcast berulang selama masih "slow" agar tidak terus-menerus
        // membersihkan cache tiap polling.
        const wasHealthy = previousStatus === "online" || previousStatus === "slow";
        if (reason === "focus" || reason === "visible" || !wasHealthy) {
          broadcastRefresh(reason);
        }
      } catch (err) {
        if (!navigator.onLine) {
          setStatus("offline");
        } else {
          failures.current += 1;
          recordEvent("fail", failureReason(err), performance.now() - startedAt);
          if (failures.current >= FAILURES_BEFORE_DOWN) {
            setStatus("server_down");
          } else {
            // Kegagalan pertama: jangan langsung menakuti user — cek ulang sebentar lagi.
            setTimeout(() => checkConnection("recheck"), QUICK_RECHECK_MS);
          }
        }
      } finally {
        checkInFlight.current = false;
      }
    },
    [broadcastRefresh, recordEvent, flushTelemetry]
  );

  useEffect(() => {
    lastStatus.current = status;
  }, [status]);

  // Polling berkala: lebih sering saat offline, jarang saat online.
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(
      () => checkConnection("poll"),
      status === "online" ? ONLINE_POLL_MS : OFFLINE_RETRY_MS
    );
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [checkConnection, status]);

  useEffect(() => {
    checkConnection("boot");

    const onOnline = () => checkConnection("online");
    const onOffline = () => setStatus("offline");
    const onFocus = () => checkConnection("focus");
    const onVisibility = () => {
      if (document.visibilityState === "visible") checkConnection("visible");
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [checkConnection]);

  return (
    <ConnectionContext.Provider value={{ status, copy: STATUS_COPY[status], lastOnlineAt, checkConnection }}>
      {children}
    </ConnectionContext.Provider>
  );
}

export const useConnection = () => useContext(ConnectionContext);
